/**
 * LexicalEditorCore — the COUPLING-FREE lexical editor implementation.
 *
 * ── Why this file exists (the lexical lib-layer split) ─────────────────────────
 * lexical + @lexical/* (~120KB+ min) plus every node subclass, plugin and the
 * serializer are bundled here and shipped OUT of app.js in `dist/lazy/lexical.js`
 * (see src/extension/lazy-entries/lexical-lazy.js + webpack.extension.lazy.js).
 *
 * Lexical registers `LexicalNode` subclasses in the editor and relies on
 * `instanceof` across the whole document model, so ALL lexical runtime code MUST
 * resolve to ONE copy. That is why the *entire* editor implementation lives in
 * this bundle (app.js keeps only `import type` + a React.lazy host) — a partial
 * split (lib in the bundle, node subclasses in app.js) would create two lexical
 * copies and break node registration / instanceof.
 *
 * ── Coupling-free contract ─────────────────────────────────────────────────────
 * This file (and its whole import subtree: ./nodes, ./plugins, ./utils/
 * serialization, ./theme) imports ONLY lexical/@lexical, react/react-dom, lodash
 * and relative modules — NEVER Redux, MUI, i18next or app thunks. The three
 * app-couplings the old LexicalMessageEditor had are now inbound props supplied
 * by the app-side host (LexicalMessageEditor.tsx):
 *   - `placeholder`             (was `useTranslation` → t('chatView.typeYourMessage'))
 *   - `onActiveFormatsChange`   (was `useAppDispatch` → dispatch(setActiveFormats))
 *   - `mentionCandidates` / `mentionsLoading` (was the Redux `useMentionCandidates`
 *     hook, called inside MentionPlugin) — now fed via MentionCandidatesContext.
 * The MUI <Box> wrapper became a plain <div> (its `sx` moved to inline style +
 * the existing <style> block).
 *
 * react/react-dom are the app's singletons via the __BT_REACT__ shim bridge
 * (webpack.extension.lazy.js aliases) so hooks/context/portals share app.js's
 * React tree.
 */
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { $insertList, ListItemNode, ListNode } from '@lexical/list';
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  EditorState,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
} from 'lexical';
import { debounce } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MentionCandidatesContext } from './mention-context';
import type { MentionCandidate } from './mention-types';
import {
  $createWhatsAppCodeBlockNode,
  $createWhatsAppQuoteNode,
  $isWhatsAppCodeBlockNode,
  $isWhatsAppQuoteNode,
  MentionNode,
  WhatsAppBoldNode,
  WhatsAppCodeBlockNode,
  WhatsAppItalicNode,
  WhatsAppMonospaceNode,
  WhatsAppQuoteNode,
  WhatsAppStrikethroughNode
} from './nodes';
import { EditorRefPlugin } from './plugins/EditorRefPlugin';
import { KeyboardBehaviorPlugin } from './plugins/KeyboardBehaviorPlugin';
import { MentionPlugin } from './plugins/MentionPlugin';
import { WhatsAppCodeBlockPlugin } from './plugins/WhatsAppCodeBlockPlugin';
import { WhatsAppInlineFormattingPlugin } from './plugins/WhatsAppInlineFormattingPlugin';
import { WhatsAppPastePlugin } from './plugins/WhatsAppPastePlugin';
import { WhatsAppQuotePlugin } from './plugins/WhatsAppQuotePlugin';
import { deserializeToEditor, serializeEditorContent } from './utils/serialization';

/**
 * Lexical Message Editor Component
 * Replaces Tiptap with Lexical for better WhatsApp markdown handling
 */

/** URL matcher for the PREVIEW-only AutoLinkPlugin (playground-standard regex).
 *  Bare "www." links get an https:// prefix so the anchor actually navigates. */
const PREVIEW_URL_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
    (text) => (text.startsWith('http') ? text : `https://${text}`),
  ),
];

export interface LexicalMessageEditorRef {
  formatBySymbol: (symbol: 'bold' | 'italic' | 'strikethrough' | 'monospace' | 'code-block' | 'bullet' | 'number' | 'quote' | '*' | '_' | '~' | '`' | '```') => void;
  insertTextAtCursor: (text: string) => void;
  insertEmojiAtCursor: (emoji: string) => void;
  storeCursorPosition: () => void;
  getCurrentListState: () => { mode: 'bullet' | 'number' | 'none'; isInList: boolean };
}

/** Active inline/block formatting flags emitted on every selection change.
 *  The app-side host maps this to `dispatch(setActiveFormats(...))`. */
export interface ActiveFormatsPayload {
  bold: boolean;
  italic: boolean;
  strike: boolean;
  code: boolean;
  codeBlock: boolean;
  quote: boolean;
}

/** Public props (same shape the consumers pass) PLUS the three severed-coupling
 *  inputs supplied by the app-side host. The extra props are optional so the
 *  core still renders standalone (e.g. in isolation tests). */
export interface LexicalEditorCoreProps {
  isPreview?: boolean;
  onFilesSelected?: (files: File[]) => void;
  content?: string; // Content is now the primary way to set value
  onChange?: (markdown: string) => void;
  /** Placeholder text (was i18n `t('chatView.typeYourMessage')`). */
  placeholder?: string;
  /** Called on every selection change with the active-format flags
   *  (was `dispatch(setActiveFormats(...))`). */
  onActiveFormatsChange?: (formats: ActiveFormatsPayload) => void;
  /** @mention candidates (was the Redux `useMentionCandidates` hook). */
  mentionCandidates?: MentionCandidate[];
  mentionsLoading?: boolean;
  /**
   * FE#384 — when true the editor stretches to fill its parent's height instead
   * of the fixed 170–400px band, so the composer can own all remaining vertical
   * space in the responsive tabbed dialog. Opt-in: every other surface keeps the
   * capped default. Requires each ancestor down to this editor to be a flex-fill
   * column (see MessageEditorControlled's own `fillHeight`).
   */
  fillHeight?: boolean;
}

const LexicalMessageEditorInternal = forwardRef<LexicalMessageEditorRef, LexicalEditorCoreProps>(
  ({ isPreview = false, onFilesSelected, content, onChange, placeholder, onActiveFormatsChange, mentionCandidates, mentionsLoading, fillHeight = false }, ref) => {
    // We strictly use the passed 'content' prop now, no internal Redux selection
    const effectiveMessage = content || '';

    const isUpdatingFromRedux = useRef(false);
    const lastEmittedContent = useRef<string | undefined>(undefined); // Track last emitted content to prevent loop
    const isInitialized = useRef(false); // Fix: Track initialization preventing overwrite
    const editorRef = useRef<LexicalEditor | null>(null);

    const initialConfig: InitialConfigType = {
      namespace: 'WhatsAppEditor',
      theme: {
        paragraph: 'editor-paragraph',
        whatsappBold: 'editor-text-bold',
        whatsappItalic: 'editor-text-italic',
        whatsappStrikethrough: 'editor-text-strikethrough',
        whatsappMonospace: 'editor-text-code',
        text: {
          bold: 'editor-text-bold',
          italic: 'editor-text-italic',
          strikethrough: 'editor-text-strikethrough',
          underline: 'editor-text-underline',
          code: 'editor-text-code',
        },
        list: {
          ul: 'editor-list-ul',
          ol: 'editor-list-ol',
          listitem: 'editor-list-item',
        },
        quote: 'editor-quote',
        code: 'editor-code-block',
        codeHighlight: {
          at: 'editor-tokenAttr',
          attr: 'editor-tokenAttr',
          boolean: 'editor-tokenProperty',
          builtin: 'editor-tokenSelector',
          cdata: 'editor-tokenComment',
          char: 'editor-tokenSelector',
          class: 'editor-tokenFunction',
          'class-name': 'editor-tokenFunction',
          comment: 'editor-tokenComment',
          constant: 'editor-tokenProperty',
          deleted: 'editor-tokenProperty',
          doctype: 'editor-tokenComment',
          entity: 'editor-tokenOperator',
          function: 'editor-tokenFunction',
          important: 'editor-tokenVariable',
          inserted: 'editor-tokenSelector',
          keyword: 'editor-tokenAttr',
          namespace: 'editor-tokenVariable',
          number: 'editor-tokenProperty',
          operator: 'editor-tokenOperator',
          prolog: 'editor-tokenComment',
          property: 'editor-tokenProperty',
          punctuation: 'editor-tokenPunctuation',
          regex: 'editor-tokenVariable',
          selector: 'editor-tokenSelector',
          string: 'editor-tokenSelector',
          symbol: 'editor-tokenProperty',
          tag: 'editor-tokenProperty',
          url: 'editor-tokenOperator',
          variable: 'editor-tokenVariable',
        },
      },
      onError: (error: Error) => {
        console.error(error);
      },
      editable: !isPreview,
      nodes: [
        ListNode,
        ListItemNode,
        WhatsAppBoldNode,
        WhatsAppItalicNode,
        WhatsAppStrikethroughNode,
        WhatsAppMonospaceNode,
        WhatsAppCodeBlockNode,
        HeadingNode,
        WhatsAppQuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
        MentionNode,
      ],
    };

    const handleEditorChange = (editorState: EditorState, editor: LexicalEditor) => {
      // Allow updates if we are not currently syncing from props
      if (!isUpdatingFromRedux.current && !isPreview) {
        editorState.read(() => {
          const markdown = serializeEditorContent(editor);
          lastEmittedContent.current = markdown; // Update our local tracking immediately

          // Call debounced onChange
          debouncedOnChange(markdown);
        });
      }
    };

    // Debounce the onChange prop to prevent Redux loop on every keystroke
    const debouncedOnChange = useMemo(
      () => debounce((markdown: string) => {
        if (onChange) {
          onChange(markdown);
        }
      }, 300), // 300ms delay
      [onChange]
    );

    // Flush (not cancel) pending debounced content on unmount / onChange swap,
    // so the last keystrokes typed within the 300ms window still reach the store.
    useEffect(() => {
      return () => {
        debouncedOnChange.flush();
      };
    }, [debouncedOnChange]);



    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      formatBySymbol: (symbol) => {
        if (isPreview || !editorRef.current) return;

        // Handle Lists using Lexical Helper ($insertList)
        if (symbol === 'bullet' || symbol === 'number') {
            editorRef.current.update(() => {
              let selection = $getSelection();

              // If no selection, create one at the end
              if (!selection) {
                const root = $getRoot();
                const lastChild = root.getLastChild();
                if (lastChild) {
                  lastChild.selectEnd();
                  selection = $getSelection();
                }
              }

              if ($isRangeSelection(selection)) {
                  $insertList(symbol === 'number' ? 'number' : 'bullet');
              }
            });
            return;
        }

        const editor = editorRef.current;

        // Use standard Lexical commands for inline formatting to ensure toggle behavior
        if (symbol === '*') {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            return;
        }
        if (symbol === '_') {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            return;
        }
        if (symbol === '~') {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            return;
        }
        if (symbol === '`') {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
            return;
        }

        editorRef.current.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const selectedText = selection.getTextContent();

          if (symbol === '```') {
            // Code block - Block Level Toggle
            const nodes = selection.getNodes();
            const topLevelElements = new Set<any>();

            nodes.forEach((node) => {
              const top = node.getTopLevelElement();
              if (top) topLevelElements.add(top);
            });

            // Fallback for collapsed selection (no nodes explicitly selected but anchor exists)
            if (topLevelElements.size === 0) {
                 const anchor = selection.anchor.getNode();
                 const top = anchor.getTopLevelElement();
                 if (top) topLevelElements.add(top);
            }

            // Check if ALL are code blocks
            const allAreCodeBlocks = Array.from(topLevelElements).every(node => $isWhatsAppCodeBlockNode(node));

            if (allAreCodeBlocks) {
                // Already code blocks -> Unwrap or Exit
                if (selection.isCollapsed()) {
                     // Exit current block (create new para after)
                     // We use the anchor's block for this
                     const anchorElement = selection.anchor.getNode().getTopLevelElement();
                     if (anchorElement) {
                         const paragraph = $createParagraphNode();
                         anchorElement.insertAfter(paragraph);
                         paragraph.selectStart();
                     }
                } else {
                     // Unwrap all selected blocks to paragraphs
                     topLevelElements.forEach(node => {
                        if ($isWhatsAppCodeBlockNode(node)) {
                             const paragraph = $createParagraphNode();
                             const children = node.getChildren();
                             children.forEach(child => paragraph.append(child));
                             node.replace(paragraph);
                        }
                     });
                }
            } else {
                // Wrap in code block (Transform)
                topLevelElements.forEach(node => {
                    // Only wrap if not already a code block and is an Element (e.g. Paragraph)
                    if (!$isWhatsAppCodeBlockNode(node) && $isElementNode(node)) {
                        const codeBlock = $createWhatsAppCodeBlockNode();
                        const children = node.getChildren();
                        children.forEach(child => codeBlock.append(child));
                        node.replace(codeBlock);
                    }
                });
            }
          } else if (symbol === 'quote') {
            // Quote - wrap each paragraph/line in its own quote node (WhatsApp style)

            // Get all selected nodes
            const nodes = selection.getNodes();

            // Check if we're already in a quote
            const anchorNode = selection.anchor.getNode();
            let topLevel = anchorNode.getTopLevelElement();

            // Helper to get out of quote if strictly inside
            if ($isWhatsAppQuoteNode(topLevel) || (topLevel && topLevel.getType() === 'quote')) {
                 // Logic to UN-quote could go here.
                 // For now, let's just not re-quote to avoid invalid nesting.
                 // A true toggle would unwrap.
                 // Simple implementation: unwrap the quote content back to paragraphs.
                 const children = topLevel.getChildren();
                 const fragment = $createParagraphNode();
                 // Actually we can't easily merge back to stream without more logic,
                 // but we can replace Quote with its children (paragraphs).
                 // Quotes in this implementation contain paragraphs.

                 // If the child is a paragraph, we promote it.
                 // topLevel is the QuoteNode.
                 if (children.length > 0) {
                     // Move children out
                     // This is a bit complex for a quick fix, so we'll stick to 'return' if already quoted for safety,
                     // effectively doing nothing (preventing double quoting).
                     return;
                 }
            }

            // Collect all paragraphs that are selected
            const paragraphsToQuote: any[] = [];
            const seenParagraphs = new Set<string>();

            // If selection matches specific nodes
            nodes.forEach((node) => {
                const top = node.getTopLevelElement();
                if (top && top.getType() === 'paragraph' && !seenParagraphs.has(top.getKey())) {
                  paragraphsToQuote.push(top);
                  seenParagraphs.add(top.getKey());
                }
            });

            // If selection is collapsed effectively, 'nodes' might just be the text node.
            // Ensure we grabbed the current paragraph if list is empty
            if (paragraphsToQuote.length === 0 && topLevel && topLevel.getType() === 'paragraph') {
                paragraphsToQuote.push(topLevel);
            }

            // Convert found paragraphs to quotes
            if (paragraphsToQuote.length > 0) {
                paragraphsToQuote.forEach((para) => {
                  const quoteNode = $createWhatsAppQuoteNode();
                  // Create new paragraph inside quote to hold content
                  const quoteParagraph = $createParagraphNode();

                  // Move children
                  const children = para.getChildren();
                  children.forEach((child: any) => {
                    quoteParagraph.append(child);
                  });

                  quoteNode.append(quoteParagraph);
                  para.replace(quoteNode);
                });
            }
          }
        });
      },
      insertTextAtCursor: (text) => {
        if (isPreview || !editorRef.current) return;

        // Mirrors `insertEmojiAtCursor`: when the editor has been BLURRED there is
        // no range selection, and the old `if (selection)` guard made this a
        // silent no-op. The personalization chips get away with it by
        // `preventDefault`-ing mousedown (focus never leaves), but any inserter
        // behind a focus-trapping surface — the "more tokens" MUI Menu — has
        // already lost the selection by the time it can call this. Fall back to
        // the end of the content so the insert always lands.
        editorRef.current.focus();
        editorRef.current.update(() => {
          let selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            $getRoot().selectEnd();
            selection = $getSelection();
          }
          if (selection) {
            selection.insertText(text);
          }
        });
      },
      insertEmojiAtCursor: (emoji) => {
        if (isPreview || !editorRef.current) return;

        editorRef.current.focus();
        editorRef.current.update(() => {
          let selection = $getSelection();
          // If no selection (editor lost focus), select end of content
          if (!$isRangeSelection(selection)) {
            $getRoot().selectEnd();
            selection = $getSelection();
          }
          if (selection) {
            selection.insertText(emoji);
          }
        });
      },
      storeCursorPosition: () => {
        // Not needed for Lexical - it maintains selection automatically
      },
      getCurrentListState: () => {
        if (!editorRef.current) return { mode: 'none' as const, isInList: false };

        let listMode: 'bullet' | 'number' | 'none' = 'none';
        let isInList = false;

        editorRef.current.getEditorState().read(() => {
          const selection = $getSelection();
          if (!selection) return;

          // Type assertion since we know it's a RangeSelection in normal editing
          const rangeSelection = selection as any;
          if (!rangeSelection.anchor) return;

          const anchorNode = rangeSelection.anchor.getNode();
          const textContent = anchorNode.getTextContent();

          // Check if current line starts with list markers
          if (textContent.trim().startsWith('•') || textContent.trim().startsWith('-')) {
            listMode = 'bullet';
            isInList = true;
          } else if (/^\d+\./.test(textContent.trim())) {
            listMode = 'number';
            isInList = true;
          }
        });

        return { mode: listMode, isInList };
      },
    }));

    return (
      <div
        className="lexical-box-root"
        style={{
          position: 'relative',
          flex: 'auto',
          width: '100%',
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          borderRadius: '0px',
          transition: 'all 0.2s ease',
          // FE#384 fill mode: become a flex-fill column so the ContentEditable
          // can claim all remaining height.
          ...(fillHeight ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' as const } : {}),
        }}
      >
        <MentionCandidatesContext.Provider
          value={{ candidates: mentionCandidates ?? [], isLoading: mentionsLoading ?? false }}
        >
        <LexicalComposer initialConfig={initialConfig}>
          <div className="editor-container" style={{ position: 'relative', ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const } : {}) }}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="lexical-editor"
                  style={{
                    // FE#384 fill mode (opt-in): grow to fill the composer column
                    // (min 120px, no max) in the responsive tabbed dialog. Otherwise
                    // prod's FE#385 CSS-var-overridable defaults (a host surface can
                    // shrink the floor on short screens; long-standing 170 / 400).
                    ...(fillHeight
                      ? { flex: '1 1 auto', minHeight: '120px', maxHeight: 'none' }
                      : { minHeight: 'var(--bt-editor-min-h, 170px)', maxHeight: 'var(--bt-editor-max-h, 400px)' }),
                    overflow: 'auto',
                    padding: '16px',
                    outline: 'none',
                    fontSize: '16px',
                    lineHeight: '1.5',
                    direction: 'inherit',
                    textAlign: 'start',
                  }}
                />
              }
              placeholder={
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    insetInlineStart: '16px',
                    color: '#999',
                    pointerEvents: 'none',
                  }}
                >
                  {isPreview ? '' : (placeholder ?? '')}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <ListPlugin />
            <OnChangePlugin onChange={handleEditorChange} />
            <EditorRefPlugin editorRef={editorRef} />
            <WhatsAppCodeBlockPlugin />
            <WhatsAppInlineFormattingPlugin />
            <WhatsAppQuotePlugin />
            <WhatsAppPastePlugin />
            <KeyboardBehaviorPlugin />
            <MentionPlugin />

            {/* PREVIEW ONLY: turn plain-text URLs into clickable links that
                open in a new tab. Not mounted in the composer — the custom
                serializer doesn't know LinkNode, and WhatsApp linkifies on
                its own side anyway. */}
            {isPreview && <AutoLinkPlugin matchers={PREVIEW_URL_MATCHERS} />}
            {isPreview && <ClickableLinkPlugin newTab />}

            {/* Sync content */}
            <SyncContentPlugin
              message={effectiveMessage}
              isUpdatingFromRedux={isUpdatingFromRedux}
              lastEmittedContent={lastEmittedContent}
              isPreview={isPreview}
            />
            <ActiveStatePlugin onActiveFormatsChange={onActiveFormatsChange} />
          </div>
        </LexicalComposer>
        </MentionCandidatesContext.Provider>

        {/* Styles */}
        <style>{`
          .lexical-box-root:focus-within {
            border-color: #51b3e2;
            box-shadow: 0 0 0 3px rgba(81, 179, 226, 0.1);
          }
          .lexical-editor {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          }

          /* WhatsApp-style message links (preview linkification) */
          .lexical-editor a {
            color: #027eb5;
            text-decoration: underline;
            cursor: pointer;
            word-break: break-all;
          }

          /* WhatsApp Formatting Styles */
          /* WhatsApp Formatting Styles */
          .editor-text-bold {
            font-weight: 700;
          }

          .editor-text-italic {
            font-style: italic;
          }

          .editor-text-strikethrough {
            text-decoration: line-through;
          }

          .editor-text-code {
            font-family: 'Courier New', Courier, monospace;
            background-color: rgba(194, 189, 184, .15);
            padding: 2px 4px;
            border-radius: 3px;
          }

          .editor-code-block {
            font-family: Consolas, Menlo, Monaco, "Lucida Console", "Liberation Mono", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Courier New", Courier, monospace;
            font-size: 13px;
            line-height: 20px;
            color: rgb(10, 10, 10);
            background-color: #f5f5f5;
            padding: 12px;
            border-radius: 4px;
            margin: 8px 0;
            white-space: pre-wrap;
            direction: ltr;
            text-align: left;
          }

          .editor-list-ul {
            list-style-type: disc;
            margin: 8px 0;
            /* Logical property so the marker + its reserved space follow text
               direction — physical padding-left leaves the bullet detached on
               the far side for RTL (Hebrew/Arabic) messages. */
            padding-inline-start: 24px;
          }

          .editor-list-ol {
            list-style-type: decimal;
            margin: 8px 0;
            padding-inline-start: 24px;
          }

          .editor-list-item {
            margin: 4px 0;
          }

          .editor-quote {
            margin: 4px 0;
            /* Logical, like the list padding above: a physical left border put
               the quote bar on the wrong side of RTL (Hebrew/Arabic) text. */
            padding-inline-start: 12px;
            border-inline-start: 3px solid #06cf9c;
            color: inherit;
          }
        `}</style>
      </div>
    );
  }
);

LexicalMessageEditorInternal.displayName = 'LexicalMessageEditorInternal';

/**
 * Slotted Wrapper for Shadow DOM compatibility
 *
 * This wrapper automatically detects if it's running inside a Shadow DOM.
 * If so, it portals the editor to the Light DOM (attached to the shadow host)
 * and uses a slot to position it.
 */
const LexicalEditorCore = forwardRef<LexicalMessageEditorRef, LexicalEditorCoreProps>(
  (props, ref) => {
    const slotContainerRef = useRef<HTMLDivElement>(null);
    const lightDOMContainerRef = useRef<HTMLDivElement | null>(null);
    const [isShadowDOM, setIsShadowDOM] = React.useState(false);

    // Generate a unique slot name for this instance to prevent collisions
    // when multiple editors are rendered in the same Shadow DOM
    const slotName = React.useMemo(() => `lexical-editor-${Math.random().toString(36).substr(2, 9)}`, []);

    useEffect(() => {
      const slotElement = slotContainerRef.current;
      if (!slotElement) return;

      const rootNode = slotElement.getRootNode();
      let container: HTMLDivElement | null = null; // Store locally for cleanup

      // Only use portal/slot mechanism if we are in Shadow DOM AND NOT in preview mode
      // User requested to skip slot mechanism for preview to avoid "ghosting" issues
      if (rootNode instanceof ShadowRoot && !props.isPreview) {
        setIsShadowDOM(true);

        // Use a local variable for the container to ensure cleanup closes over the right element
        container = document.createElement('div');
        container.className = 'lexical-editor-light-dom-container';

        // CRITICAL: Set the UNIQUE slot attribute so it projects into the specific slot for this instance
        container.setAttribute('slot', slotName);

        // CRITICAL: Set width into the container itself
        // Note: width on display:contents is ignored, but we set it just in case logic changes
        container.style.cssText = 'display: contents; width: 100%;';

        // Find the shadow host and append to it
        const shadowHost = rootNode.host;
        shadowHost.appendChild(container);
        lightDOMContainerRef.current = container;
      } else {
        setIsShadowDOM(false);
      }

      return () => {
        // Cleanup: remove the light DOM container when component unmounts
        // We use the LOCAL `container` variable which captures the specific element created in this effect execution
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
        lightDOMContainerRef.current = null;
      };
    }, [slotName, props.isPreview]);

    if (!isShadowDOM) {
      // Normal rendering for Light DOM (or Preview Mode)
      // We pass 100% width via the container div style as well, to align with user request
      return (
        <div ref={slotContainerRef} style={{ display: 'contents', width: '100%' }}>
           <LexicalMessageEditorInternal ref={ref} {...props} />
        </div>
      );
    }

    return (
      <>
        {/* Slot element in Shadow DOM - using unique name */}
        <div ref={slotContainerRef} style={{ display: 'contents', width: '100%' }}>
          <slot name={slotName}></slot>
        </div>

        {/* Portal the actual Lexical editor to light DOM */}
        {lightDOMContainerRef.current && createPortal(
          <LexicalMessageEditorInternal ref={ref} {...props} />,
          lightDOMContainerRef.current
        )}
      </>
    );
  }
);

LexicalEditorCore.displayName = 'LexicalEditorCore';

/**
 * Plugin to sync editor content from Redux or Prop
 */
function SyncContentPlugin({
  message,
  isUpdatingFromRedux,
  lastEmittedContent,
  isPreview,
}: {
  message: string | undefined;
  isUpdatingFromRedux: React.MutableRefObject<boolean>;
  lastEmittedContent: React.MutableRefObject<string | undefined>;
  isPreview: boolean;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // If message is passed and we are not currently updating *from* the Redux/parent loop, sync it.
    // The parent controls the source of truth now.
    // We also check if the message matches what we last emitted to avoid resetting cursor on round-trip.
    //
    // The `isUpdatingFromRedux` guard exists ONLY to break the edit-mode round
    // trip (editor → onChange → redux → content). In PREVIEW mode there is no
    // onChange (handleEditorChange early-returns when isPreview), so the guard
    // has no purpose — and it actively HARMS the preview: its reset is scheduled
    // on a `setTimeout(0)` macrotask, so when the `content` prop changes twice
    // within one tick (e.g. a drip step-switch mirrors the stale-then-correct
    // value into messageDraft in the same commit), the second/correct update is
    // dropped and the preview stays pinned on the previous value. So in preview
    // we bypass the flag entirely and always sync the latest content.
    const canSync = isPreview || !isUpdatingFromRedux.current;
    if (message !== undefined && canSync && message !== lastEmittedContent.current) {
        if (!isPreview) {
            // We set this flag to prevent the OnChange loop from firing back
            // immediately and causing an infinite loop if the serialized content
            // differs slightly. Reset after a tick to allow user edits again.
            isUpdatingFromRedux.current = true;
            setTimeout(() => {
                isUpdatingFromRedux.current = false;
            }, 0);
        }

        deserializeToEditor(message, editor);
        lastEmittedContent.current = message; // Update local ref so we don't re-sync
    }
  }, [message, editor, isUpdatingFromRedux, lastEmittedContent, isPreview]);

  return null;
}

/**
 * Plugin to track active formatting state.
 * Emits the flags via the `onActiveFormatsChange` callback supplied by the
 * app-side host (which maps it to `dispatch(setActiveFormats(...))`).
 */
function ActiveStatePlugin({
  onActiveFormatsChange,
}: {
  onActiveFormatsChange?: (formats: ActiveFormatsPayload) => void;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
             // Dispatch defaults if no selection? Or keep previous?
             // Usually better to reset to false if lost focus or selection is null
             onActiveFormatsChange?.({
                bold: false,
                italic: false,
                strike: false,
                code: false,
                codeBlock: false,
                quote: false
             });
             return;
        }

        const isBold = selection.hasFormat('bold');
        const isItalic = selection.hasFormat('italic');
        const isStrike = selection.hasFormat('strikethrough');
        const isCode = selection.hasFormat('code');

        const anchorNode = selection.anchor.getNode();
        // Fallback if no top level element (e.g. root)
        const element = anchorNode.getTopLevelElement();

        let isCodeBlock = false;
        let isQuote = false;

        if (element) {
             isCodeBlock = $isWhatsAppCodeBlockNode(element);

             const parent = element.getParent();
             if ($isWhatsAppQuoteNode(element) ||
                 ($isWhatsAppQuoteNode(parent)) ||
                 element.getType() === 'quote' ||
                 parent?.getType() === 'quote') {
                 isQuote = true;
             }
        }

        onActiveFormatsChange?.({
            bold: isBold,
            italic: isItalic,
            strike: isStrike,
            code: isCode,
            codeBlock: isCodeBlock,
            quote: isQuote
        });
      });
    });
  }, [editor, onActiveFormatsChange]);

  return null;
}

export default LexicalEditorCore;
